import appConfig from '../../config/appConfig';
import helpers from '../../utils/helpers';
import framesProcessingService from '../device/framesProcessingService';
import deviceConnectionDetectorService from '../deviceConnectionDetectorService';
import DeviceRequestTimeoutError from '../errors/deviceRequestTimeoutError';
import ReconnectEventEmitter from '../events/reconnectEventEmitter';
import iotService from '../iot/iotService';
import log from '../logger/log';
import pairingService from '../pairingService';
import userDeviceService from '../user/userDeviceService';
import IqosBleClient from './iqosBleClient';

const DEVICE_REQUEST_TIMEOUT_MS = 10 * 1000;

const connect = async ({isNewDevice, onConnect, onError, onDeviceSelect, retryNumber}) => {
    try {
        let deviceSelectTimeout;

        const options = {
            onDeviceConnect: () => {
                pairingService.addBleAvailabilityListener();
                framesProcessingService.createNew();
            },
            onDisconnected: async (isDisconnectedByUser) => {
                pairingService.onDeviceDisconnect(isDisconnectedByUser);
            },
            onForcedDisconnect: () => {
                pairingService.disconnectAndForwardToMyDevicesPage();
            },
            onDeviceReconnectSuccess: () => {
                const reconnectEventEmitter = new ReconnectEventEmitter();

                reconnectEventEmitter.emitReconnect();
                iotService.initIotService({isReconnect: true, throwException: false});
            },
            onReconnectFail: async () => {
                deviceConnectionDetectorService.showFailedReconnectDeviceGuide({isReconnectFailed: true});
            },
            onDeviceSelect: () => {
                helpers.runFunction(onDeviceSelect);
                const deviceSelectPromise = new Promise((res, rej) => {
                    deviceSelectTimeout = setTimeout(() => {
                        rej(new DeviceRequestTimeoutError());
                    }, DEVICE_REQUEST_TIMEOUT_MS);
                });

                deviceSelectPromise.catch((e) => {
                    helpers.runFunction(onError, [e]);
                });
            },
            onFinishDeviceSelect: () => clearTimeout(deviceSelectTimeout),
        };

        const iqosBleClient = new IqosBleClient(options);

        await iqosBleClient.connect({
            isNewDevice,
            onConnect,
            onError,
            onReadingCharacteristics: async (deviceName) => onReadingCharacteristics({deviceName, retryNumber}),
        });
    } catch (e) {
        log.debug(`iqosBleService: device connection failed, error: ${e}`);
        helpers.runFunction(onError, [e]);
        throw e;
    }
};

// All names should be in upper case
const DEVICES_WITHOUT_INITIAL_PAIRING_PROBLEM_ON_WINDOWS = [
    'DUO',
    'MY IQOS 2.4+',
    'IQOS ORIGINALS DUO',
    'IQOS 3 MULTI',
    'MY IQOS 3',
    'VEEV',
];
const PAIRING_DElAY_MS = 4000;

const onReadingCharacteristics = async ({deviceName, retryNumber}) => {
    const delay = getPairingDelay({deviceName, retryNumber});

    await helpers.timeout(delay);
};
const getPairingDelay = ({deviceName, retryNumber}) => {
    const MAX_MULTIPLIER = 2;

    if (retryNumber > MAX_MULTIPLIER) return MAX_MULTIPLIER * PAIRING_DElAY_MS;
    if (retryNumber) return retryNumber * PAIRING_DElAY_MS;

    const firstTryPairingDelay = getFirstTryPairingDelay(deviceName);

    if (firstTryPairingDelay) return firstTryPairingDelay;

    return 0;
};

const getFirstTryPairingDelay = (deviceName) => {
    const isPairingDelayOnWindowsEnabled = appConfig.getIsPairingDelayOnWindowsEnabled();
    const isWindows = userDeviceService.isDesktopWindows();
    const isDelayRequiredForDevice = !DEVICES_WITHOUT_INITIAL_PAIRING_PROBLEM_ON_WINDOWS.includes(
        deviceName.toUpperCase()
    );
    const isInitialPairingDelayEnabled = isWindows && isPairingDelayOnWindowsEnabled && isDelayRequiredForDevice;

    if (isInitialPairingDelayEnabled) return PAIRING_DElAY_MS;
};

const isClientInitialized = () => {
    try {
        new IqosBleClient();

        return true;
    } catch (e) {
        return false;
    }
};

const disconnect = () => {
    try {
        const iqosBleClient = new IqosBleClient();

        iqosBleClient.disconnect();
        iqosBleClient.destroy();
    } catch (e) {
        log.info(`iqosBleService: disconnect() error: ${e}`);
    }
};

const isDeviceConnected = () => {
    try {
        const iqosBleClient = new IqosBleClient();

        return iqosBleClient.isDeviceConnected();
    } catch (e) {
        log.debug(`iqosBleService: isDeviceConnected() error: ${e}`);

        return false;
    }
};

const unmount = () => {
    try {
        const iqosBleClient = new IqosBleClient();

        iqosBleClient.unmount();
    } catch (e) {
        log.info(`iqosBleService: unmount() error: ${e}`);
    }
};

export default {
    connect,
    disconnect,
    isClientInitialized,
    isDeviceConnected,
    unmount,
};
